import React, { useState } from 'react';
import { Bell, BellOff, Zap, Loader2, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationSettingsDialog = ({ open, onOpenChange }) => {
  const {
    isSupported,
    isPushSubscribed,
    subscribeToPush,
    unsubscribeFromPush,
    sendServerTestPush,
  } = useNotifications();

  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const toggle = async () => {
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      if (isPushSubscribed) {
        await unsubscribeFromPush();
        setSuccess('Push notifications disabled.');
      } else {
        await subscribeToPush();
        setSuccess('Push notifications enabled. You can send yourself a test below.');
      }
    } catch (e) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setError(null);
    setSuccess(null);
    setTesting(true);
    try {
      await sendServerTestPush();
      setSuccess('Test push sent from the server. Watch for the notification in a moment.');
    } catch (e) {
      setError(e.message || 'Test push failed.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="notification-settings-dialog"
        className="sm:max-w-md bg-[#050810] border-[#1e2a45]"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#F97316]" />
            Push Notifications
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Get real-time alerts when your submitted markets are approved, when
            markets you created go live, and when markets you traded on resolve.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {!isSupported && (
            <div className="bg-[#2d1512] border border-red-900/50 rounded-lg p-3 text-sm text-red-300">
              Push notifications are not supported by this browser.
            </div>
          )}

          <div className="flex items-center justify-between bg-[#131b2e] border border-[#1e2a45] rounded-xl p-4">
            <div className="flex items-center gap-3 min-w-0">
              {isPushSubscribed ? (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#22C55E] to-[#15803D] flex items-center justify-center flex-shrink-0">
                  <Bell className="h-5 w-5 text-white" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#1e2a45] flex items-center justify-center flex-shrink-0">
                  <BellOff className="h-5 w-5 text-gray-400" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">
                  {isPushSubscribed ? 'Enabled on this device' : 'Not enabled'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {isPushSubscribed
                    ? 'You will receive browser notifications even when AMP is closed.'
                    : 'Enable to receive real-time browser push alerts.'}
                </p>
              </div>
            </div>
            <Button
              onClick={toggle}
              disabled={busy || !isSupported}
              data-testid="toggle-push-button"
              className={
                isPushSubscribed
                  ? 'bg-[#1e2a45] text-white hover:bg-[#2d3a56]'
                  : 'bg-gradient-to-b from-[#F97316] to-[#EA580C] text-white hover:from-[#EA580C] hover:to-[#C2410C]'
              }
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isPushSubscribed ? (
                'Disable'
              ) : (
                'Enable'
              )}
            </Button>
          </div>

          {isPushSubscribed && (
            <Button
              onClick={sendTest}
              disabled={testing}
              variant="outline"
              data-testid="send-test-push-button"
              className="w-full bg-[#131b2e] border-[#1e2a45] text-white hover:bg-[#1e2a45]"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Zap className="h-4 w-4 mr-2 text-[#F97316]" />
              )}
              Send me a test push
            </Button>
          )}

          {error && (
            <div
              data-testid="notification-settings-error"
              className="bg-[#2d1512] border border-red-900/50 rounded-lg p-3 text-sm text-red-300"
            >
              {error}
            </div>
          )}
          {success && (
            <div
              data-testid="notification-settings-success"
              className="bg-[#0a2616] border border-green-900/50 rounded-lg p-3 text-sm text-green-300 flex items-start gap-2"
            >
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationSettingsDialog;
